import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Seo from '../components/Seo';
import { DIVISIONS, SHIPPING_PARTNERS, OEM_PARTNERS } from '../mock';
import { Calendar, CheckCircle2, Truck, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import BrandLogo from '../components/BrandLogo';
import { getEnquiryTeam, getEnquiryMailto } from '../lib/enquiries';

export default function DivisionDetail() {
  const { slug } = useParams();
  const d = DIVISIONS.find(x => x.slug === slug);
  if (!d) return <Navigate to="/divisions" replace />;

  return (
    <div>
      <Seo title={`${d.name} | Fixed Group`} description={`${d.tagline} ${d.summary.slice(0, 120)}`} />
      <PageHeader title={d.name} crumbs={[{ label: 'Divisions', to: '/divisions' }, { label: d.short }]} />

      <section className="py-16">
        <div className="fx-container grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <img src={d.image} alt={d.name} className="w-full h-80 object-cover rounded-xl shadow-lg" />
            <div className="mt-8">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-600 font-semibold"><Calendar size={14} /> Established {d.established}</div>
              <h2 className="section-title text-3xl md:text-4xl font-extrabold mt-2">About {d.short}</h2>
              <p className="mt-6 text-slate-600 leading-relaxed">{d.summary}</p>
              <p className="mt-3 text-slate-600 leading-relaxed italic">{d.tagline}</p>
            </div>

            <div className="mt-10">
              <h3 className="text-xl font-bold text-slate-900">Key Services</h3>
              <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {d.services.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-700"><CheckCircle2 className="text-emerald-500 mt-0.5" size={18} /><span>{s}</span></li>
                ))}
              </ul>
            </div>

            {d.fleet && (
              <div className="mt-10">
                <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Truck className="text-emerald-600" /> Fleet Overview</h3>
                <div className="mt-4 grid sm:grid-cols-2 gap-6">
                  <div className="fx-card p-6">
                    <div className="text-xs uppercase tracking-widest text-emerald-600">Prime Movers</div>
                    <ul className="mt-3 space-y-1.5 text-slate-700">
                      {d.fleet.primeMovers.map((p, i) => <li key={i} className="font-mono text-sm">{p}</li>)}
                    </ul>
                  </div>
                  <div className="fx-card p-6">
                    <div className="text-xs uppercase tracking-widest text-emerald-600">Chassis (40ft)</div>
                    <ul className="mt-3 space-y-1.5 text-slate-700">
                      {d.fleet.chassis.map((c, i) => <li key={i} className="font-mono text-sm">{c}</li>)}
                    </ul>
                  </div>
                </div>
                <p className="mt-4 text-sm text-slate-500">All prime movers are GPS compliant and monitored in real-time. Additional units and 20ft trailer chassis are in the pipeline.</p>
              </div>
            )}

            {d.slug === 'fixed-chiller-freezer' && (
              <div className="mt-10 rounded-xl p-8 bg-gradient-to-br from-emerald-50 to-sky-50 border border-emerald-100">
                <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Innovation Spotlight</div>
                <h3 className="mt-2 text-2xl font-extrabold text-slate-900">RRMS — Roaming Reefer Monitoring System</h3>
                <p className="mt-4 text-slate-700 leading-relaxed">RRMS uses the RME (Refrigerated Monitoring Electronics) device to connect reefer units directly to a monitoring computer via Wi-Fi or LAN. Powered by RRCE (Roaming Reefer Communication Engine), it enables real-time data transfer, alarm detection, and integration with port TOS systems.</p>
                <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-slate-700 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle2 className="text-emerald-500 mt-0.5" size={16} />700+ systems deployed worldwide</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="text-emerald-500 mt-0.5" size={16} />Reduce claims with electronic evidence</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="text-emerald-500 mt-0.5" size={16} />Downloads temperature, alarms, PTI, USDA logs</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="text-emerald-500 mt-0.5" size={16} />Integrates with TOS & loading computer</li>
                </ul>
              </div>
            )}
          </div>

          <aside className="space-y-6">
            <div className="fx-card p-6">
              <h4 className="font-bold text-slate-900">Contact us about {d.short}</h4>
              <p className="mt-2 text-sm text-slate-600">Speak with our team to tailor a service package for your operation.</p>
              <a href={getEnquiryMailto(d.slug)} className="mt-4 block text-sm font-semibold text-emerald-700 break-all underline underline-offset-4">{getEnquiryTeam(d.slug).email}</a>
              <Button asChild className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white"><Link to={`/contact?team=${encodeURIComponent(d.slug)}`}>Enquire with this team <ArrowRight className="ml-2" size={16} /></Link></Button>
            </div>

            <div className="fx-card p-6">
              <h4 className="font-bold text-slate-900">Shipping line partners</h4>
              <div className="mt-4 grid grid-cols-3 gap-3 items-center">
                {SHIPPING_PARTNERS.map((p) => (
                  <div key={p.name} className="h-12 flex items-center justify-center rounded-md border border-slate-100 bg-slate-50 p-2">
                    <BrandLogo name={p.name} logo={p.logo} className="max-h-8 max-w-full" />
                  </div>
                ))}
              </div>
            </div>

            <div className="fx-card p-6">
              <h4 className="font-bold text-slate-900">OEM Certified</h4>
              <div className="mt-4 grid grid-cols-2 gap-3 items-center">
                {OEM_PARTNERS.map((p) => (
                  <div key={p.name} className="h-14 flex items-center justify-center rounded-md border border-slate-100 bg-slate-50 p-2">
                    <BrandLogo name={p.name} logo={p.logo} className="max-h-10 max-w-full" />
                  </div>
                ))}
              </div>
            </div>

            <div className="fx-card p-6">
              <h4 className="font-bold text-slate-900">Other Divisions</h4>
              <ul className="mt-3 space-y-2">
                {DIVISIONS.filter(x => x.slug !== d.slug).map((o) => (
                  <li key={o.slug}>
                    <Link to={`/divisions/${o.slug}`} className="text-sm text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-2">{o.short} — {o.name} <ArrowRight size={14} /></Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
